import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import { queryKeysDataExchange } from './queryKeys';
import {
  EMPTY_REQUEST_VALUES,
  type SupplierOverviewParams,
  toSupplierOverviewRequest,
  zodSupplierOverviewFilters,
  zodSupplierOverviewPagination,
} from './supplierOverviewRequest';

type CustomFieldOptionsResponse = Blob;

export type DataExchangeExcelDataType = SupplierOverviewParams & {
  hasResponsiblePerson: boolean;
};

/**
 * Fetches filtered data for Excel export from the API as a Blob.
 * The filters are sent in the POST body to avoid URL length limits; sorting and paging stay in the query string.
 *
 * @param {DataExchangeExcelDataType} dataExchangeExcelData - Filter parameters for the request.
 * @returns {Promise<CustomFieldOptionsResponse>} Excel file data as a Blob.
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/export_supplier_overview_excel_data_exchange_company_information_excel_post
 * @throws {Error} If the API request fails.
 */
export const fetchDataExchangeExcelData = async (
  dataExchangeExcelData: DataExchangeExcelDataType,
) => {
  const { json, searchParams } = toSupplierOverviewRequest(
    dataExchangeExcelData,
  );

  const response = await vestigasApi
    .post(ENDPOINT.DATA_EXCHANGE.GET_EXCEL_DATA(), {
      json,
      searchParams,
      validate: {
        body: zodSupplierOverviewFilters,
        searchParams: zodSupplierOverviewPagination,
      },
    })
    .blob();

  return response;
};

/**
 * React Query hook to fetch filtered data for Excel export.
 * @param {DataExchangeExcelData} dataExchangeExcelData - Filter parameters for the request.
 * @param {UseQueryOptions} [options] - Additional query options.
 * @returns {UseQueryResult<CustomFieldOptionsResponse>} Query result with Excel data.
 */
export const useQueryDataExchangeExcelData = (
  dataExchangeExcelData: DataExchangeExcelDataType,
  options?: Omit<
    UseQueryOptions<CustomFieldOptionsResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    queryFn: async () => fetchDataExchangeExcelData(dataExchangeExcelData),
    // Key on what is actually sent, so '' and omitted share one cache entry.
    queryKey: queryKeysDataExchange.getDataAsExcel(
      withoutObjectKeysWhereValueIs(
        dataExchangeExcelData,
        EMPTY_REQUEST_VALUES,
      ),
    ),
    ...options,
  });
